/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {config} from "../config";

const getSignUrl = async (image: File) => {
    const writeSignUrl = 'v1/owner-admin/get-write-signed-url';
    const suffix = image.name.split(".").pop();
    const jwt = sessionStorage.getItem("jwt")
    const validateHeaders = {
        Authorization: `Bearer ${jwt}`,
        'Content-Type': 'application/json'
    }

    const response = await fetch(`${config.messagingUrl}/${writeSignUrl}?mimeType=${suffix}`, {
        method: 'GET',
        headers: validateHeaders
    });
    if (!response.ok) {
        throw new Error(`Failed to get write signed url: ${response.status}`)
    }
    const urlResponse: {
        signedUrl: string,
        bucketPointer: { bucketName: string, path: string },
        mimeType: string,
    } = await response.json();
    return urlResponse;
};

const uploadImage = async (signedResponse: { signedUrl: string, mimeType: string }, image: File) => {
    const response = await fetch(signedResponse.signedUrl, {
        method: 'PUT',
        body: image,
        headers: {
            'Content-Type': signedResponse.mimeType
        }
    });
    if (!response.ok) {
        throw new Error(`Failed to upload image: ${response.status}`)
    }
    return response;
};

const getDate = (): string => {
    const dateNow = new Date();
    const day = String(dateNow.getDate()).padStart(2, '0');
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const month = monthNames[dateNow.getMonth()];

    return `${month} ${day}`
}

const insertToUnitCloud = async (bucketDatas: { bucketPointer: { bucketName: string, path: string } }[], isMobile: boolean) => {
    const jwt = sessionStorage.getItem("jwt");

    let agent_id = "N/A";
    if (jwt)
        agent_id = decodeJWT(jwt)["unitId"];

    const descriptionText = `This is a picture that you uploaded on ${getDate()}`;

    const unitAssets: {}[] = await Promise.all(bucketDatas.map(async bucketData => {
        return {
            description: descriptionText,
            asset_type: "picture-plan",
            bucketPointer: bucketData.bucketPointer
        };
    }))

    const response: Response = await fetch(`${config.messagingUrl}/v1/owner-admin/insert-asset-and-notify`, {
        method: 'POST',
        body: JSON.stringify({
            assets: {
                assetsDetails: unitAssets,
                key: "picture-plan",
                mimeType: "image/png",
                metadata: {
                    origin: "upload_image_picture_frame",
                    typeOfImage: "user_generated",
                    uploadSource: isMobile ? "phone" : "web"

                }
            },
            unit: {
                product: "elliq",
                unitId: agent_id
            }
        }),
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to upload image: ${response.status}`)
    }
    return response;
};

export const sendImages = async (selectedImages: File[], isMoblie: boolean) => {
    try {
        const signUrlPromises = selectedImages.map(getSignUrl);
        const signedUrlsResponse = await Promise.all(signUrlPromises);

        await Promise.all(signedUrlsResponse.map((_signedUrlsResponse, index) => uploadImage(_signedUrlsResponse, selectedImages[index])));

        const bucketDatas: { bucketPointer: { bucketName: string, path: string } }[] = signedUrlsResponse.map(({bucketPointer}) => ({bucketPointer}));

        await insertToUnitCloud(bucketDatas, isMoblie);

        console.log('Images uploaded successfully');
        return true;
    } catch (error) {
        console.error('Error uploading images: ', error);
        return false;
    }
};

export const decodeJWT = (token: string) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
}